/**
 * TeamCard component - displays team information with members and user role
 */

import React from 'react';
import {
  Box,
  Typography,
  Card,
  Avatar,
  Chip,
  Tooltip,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import type { TeamItem, TeamRoleItem } from '@/types/team';

interface TeamCardProps {
  team: TeamItem;
  isMobile: boolean;
}

/**
 * OverlappingAvatarStack component - displays up to 5 avatars overlapping
 */
interface OverlappingAvatarStackProps {
  members: Array<{ profile_image?: string | null; name?: string | null; email?: string | null }>;
  totalCount: number;
  maxVisible?: number;
  size?: number;
  overlap?: number;
}

const OverlappingAvatarStack: React.FC<OverlappingAvatarStackProps> = ({
  members,
  totalCount,
  maxVisible = 5,
  size = 30,
  overlap = 15,
}) => {
  const visibleMembers = members.slice(0, maxVisible);
  const remainingCount = totalCount > maxVisible ? totalCount - maxVisible : 0;

  const getInitials = (name: string | null | undefined, email: string | null | undefined): string => {
    if (name) {
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return '?';
  };

  const getDisplayName = (name: string | null | undefined, email: string | null | undefined): string => {
    if (name) {
      return name;
    }
    if (email) {
      return email;
    }
    return 'Unknown';
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', height: size }}>
      {visibleMembers.map((member, index) => (
        <Tooltip
          key={index}
          title={getDisplayName(member.name, member.email)}
          placement="top"
          arrow
        >
          <Avatar
            src={member.profile_image || undefined}
            alt={member.name || member.email || 'Member'}
            sx={{
              width: size,
              height: size,
              fontSize: size * 0.4,
              border: '2px solid',
              borderColor: 'background.paper',
              position: index === 0 ? 'relative' : 'absolute',
              left: index === 0 ? 0 : `${index * (size - overlap)}px`,
              zIndex: maxVisible - index,
              bgcolor: 'primary.main',
              cursor: 'pointer',
            }}
          >
            {getInitials(member.name, member.email)}
          </Avatar>
        </Tooltip>
      ))}
      {remainingCount > 0 && (
        <Tooltip
          title={`${remainingCount} more ${remainingCount === 1 ? 'member' : 'members'}`}
          placement="top"
          arrow
        >
          <Avatar
            sx={{
              width: size,
              height: size,
              fontSize: size * 0.4,
              border: '2px solid',
              borderColor: 'background.paper',
              position: 'absolute',
              left: `${visibleMembers.length * (size - overlap)}px`,
              zIndex: 0,
              bgcolor: 'grey.400',
              color: 'common.white',
              cursor: 'pointer',
            }}
          >
            +{remainingCount}
          </Avatar>
        </Tooltip>
      )}
    </Box>
  );
};

/**
 * Get role display label
 */
const getRoleLabel = (role: 'SERVICE_MANAGER' | 'SERVICE_MEMBER' | 'OWNER' | string): string => {
  switch (role) {
    case 'OWNER':
      return 'Owner';
    case 'SERVICE_MANAGER':
      return 'Manager';
    case 'SERVICE_MEMBER':
      return 'Member';
    default:
      return role;
  }
};

/**
 * Get role color
 */
const getRoleColor = (role: 'SERVICE_MANAGER' | 'SERVICE_MEMBER' | 'OWNER' | string): 'primary' | 'default' => {
  switch (role) {
    case 'OWNER':
      return 'primary';
    case 'SERVICE_MANAGER':
      return 'primary';
    case 'SERVICE_MEMBER':
      return 'default';
    default:
      return 'default';
  }
};

export const TeamCard: React.FC<TeamCardProps> = ({ team, isMobile }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user: currentUser } = useAuth();

  // Use roles from team object if available, otherwise fetch separately
  const rolesFromTeam: TeamRoleItem[] = team.roles || [];
  const members = rolesFromTeam.map((role: TeamRoleItem) => role.user);
  const memberCount = members.length;

  // Check if current user is the team owner (creator)
  const isOwner = currentUser && team.owner && currentUser.key === team.owner.key;

  // Find current user's role in this team from role_user table
  const currentUserRoleFromRoles = currentUser
    ? rolesFromTeam.find((role: TeamRoleItem) => role.user.key === currentUser.key)?.name
    : null;

  // Prioritize owner role (creator) over role_user role
  const currentUserRole = isOwner ? 'OWNER' : currentUserRoleFromRoles;

  // Get creator/owner name
  const creatorName = team.owner?.name || team.owner?.username || team.owner?.email || 'Unknown';

  const handleCardClick = () => {
    navigate(`/teams/${team.key}`);
  };

    return (
      <Card
        sx={{
          cursor: 'pointer',
                    '&:hover': { 
          bgcolor: 'action.hover',
          boxShadow: theme.shadows[4],
        },
        transition: 'all 0.2s ease-in-out',
        p: 2.5,
      }}
      onClick={handleCardClick}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Header: Team Name and User Role */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
              variant={isMobile ? 'h6' : 'h5'}
              component="h3"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                mb: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
              }}
            >
              {team.name}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: '0.875rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              Created by {creatorName}
            </Typography>
        </Box>
          {currentUserRole && (
            <Chip
              label={getRoleLabel(currentUserRole)}
              color={getRoleColor(currentUserRole)}
              size="small"
              sx={{
                fontWeight: 500,
                flexShrink: 0,
              }}
            />
          )}
        </Box>

        {/* Members Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
            {memberCount > 0 ? (
              <>
                <Box 
                  sx={{ 
                    position: 'relative', 
                    width: `${Math.min(memberCount, 5) * (30 - 15) + 30}px`, 
                    height: 30 
                  }}
                >
                  <OverlappingAvatarStack
                    members={members}
                    totalCount={memberCount}
                    maxVisible={5}
                    size={30}
                    overlap={15}
                  />
          </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', ml: 1 }}>
                  {memberCount} {memberCount === 1 ? 'member' : 'members'}
            </Typography>
              </>
            ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                No members
            </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Card>
  );
};
